import { faList } from "@fortawesome/free-solid-svg-icons";
import { faFilter, faTable, faXmarkLarge } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { parseAsInteger, parseAsStringEnum, useQueryState } from "nuqs";
import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { SingleValue } from "react-select";
import { Button, Form, Header, Icon, Input, Message, Segment } from "semantic-ui-react";
import { Flyout, useFlyout } from "../../../../../../../components/global_components/Flyout";
import {
  AccessibleDropdown,
  AccessibleDropdownOption,
} from "../../../../../../../components/global_components/form_components/accessible/AccessibleDropdown";
import { useDebounce } from "../../../../../../../hooks/useDebounce";
import { useCoreDepartments } from "../../../../../../../hooks/useDepartments";
import { GetNotifyNotificationStatus } from "../../../../../../../queries/notify";
import { IconToggleTabBar } from "../../../../../../global/components/tab-lists/IconToggleTabBar";
import { TabBar } from "../../../../../../global/components/tab-lists/TabBar";
import useSite from "../../../../../../permits/components/inspections/InspectionPanel/useSite";
import { NotifyTopNav } from "../../../../../components/NotifyTopNav";
import { useInfiniteNotificationContent } from "../../../../../hooks/useInfiniteNotificationContent";
import { useNotifySubscriptionGroups } from "../../../../../hooks/useNotifySubscriptionGroups";
import { NotifyNotificationsCardInfiniteList } from "./components/NotifyNotificationsCardInfiniteList";
import { NotifyNotificationsTableInfiniteList } from "./components/NotifyNotificationsTableInfiniteList";
import { useIsScrollPositionTop } from "../../hooks/useIsScrollPositionTop";
import { useNotifyUserDepartments } from "./hooks/useNotifyUserDepartments";
import { NotifyNotificationsEmptyList } from "./components/NotifyNotificationsEmptyList";

const StatusFilterTab = {
  All: "all",
  Drafts: "drafts",
  Pending: "pending",
  Scheduled: "scheduled",
  Active: "active",
  Archived: "archived",
} as const;
type StatusFilterTab = (typeof StatusFilterTab)[keyof typeof StatusFilterTab];

const ViewToggleTab = {
  List: "list",
  Grid: "grid",
} as const;
type ViewToggleTab = (typeof ViewToggleTab)[keyof typeof ViewToggleTab];

function convertStatusEnum(status: StatusFilterTab): GetNotifyNotificationStatus | null {
  if (status === StatusFilterTab.All) return null;
  switch (status) {
    case StatusFilterTab.Drafts:
      return "Draft";
    case StatusFilterTab.Pending:
      return "Pending";
    case StatusFilterTab.Scheduled:
      return "Scheduled";
    case StatusFilterTab.Active:
      return "Active";
    case StatusFilterTab.Archived:
      return "Archived";
    default:
      return null;
  }
}

export function NotifyNotificationsPage() {
  const site = useSite();
  const titleBarRef = useRef<HTMLDivElement>(null);

  const [shouldShowCheckboxes, setShouldShowCheckboxes] = useState(false);
  const [view, setView] = useQueryState(
    "view",
    parseAsStringEnum<ViewToggleTab>(Object.values(ViewToggleTab)).withDefault(ViewToggleTab.Grid),
  );
  const [statusFilter, setStatusFilter] = useQueryState(
    "status",
    parseAsStringEnum<StatusFilterTab>(Object.values(StatusFilterTab)).withDefault(StatusFilterTab.All),
  );
  const [queryFilter, setQueryFilter] = useQueryState("query", { defaultValue: "", clearOnDefault: true });
  const [subscriptionGroupFilter, setSubscriptionGroupFilter] = useQueryState("subscriptionGroup", parseAsInteger);
  const [departmentFilter, setDepartmentFilter] = useQueryState("departmentGroup", parseAsInteger);

  const { close: closeFilterFlyout, open: openFilterFlyout, isOpen: isFilterFlyoutOpen } = useFlyout();

  const { subscriptionGroups, isSubscriptionGroupsLoading } = useNotifySubscriptionGroups();
  const subscriptionGroupOptions =
    subscriptionGroups?.map((group) => ({
      key: group.id,
      value: group.id,
      label: group.name,
    })) ?? [];
  const subscriptionGroupFilterOption = subscriptionGroupOptions.find(
    (option) => option.value === subscriptionGroupFilter,
  );

  const { departments, isLoading: isDepartmentsLoading } = useNotifyUserDepartments({
    limitToUsersRoles: statusFilter === StatusFilterTab.Drafts,
  });
  const departmentOptions = [
    ...(departments?.map((department) => ({
      key: department.id,
      value: department.id,
      label: department.name,
    })) ?? []),
  ];
  const departmentFilterOption = departmentOptions.find((option) => option.value === departmentFilter);

  const scrollElement = useMemo(() => document.querySelector("main"), []);
  const isScrollPositionTop = useIsScrollPositionTop(scrollElement);

  const filters = useMemo(() => {
    return {
      query: queryFilter,
      subscriptionGroupIds: subscriptionGroupFilter ? [subscriptionGroupFilter] : [],
      departmentGroupId: departmentFilter,
      status: convertStatusEnum(statusFilter),
    };
  }, [queryFilter, subscriptionGroupFilter, departmentFilter, statusFilter]);
  const debouncedFilters = useDebounce(filters, 300);

  const {
    total: totalNotifications,
    isNotificationContentFetching,
    statuses,
  } = useInfiniteNotificationContent({
    take: 50,
    query: debouncedFilters.query,
    subscriptionGroupIds: debouncedFilters.subscriptionGroupIds,
    departmentGroupId: debouncedFilters.departmentGroupId,
    status: debouncedFilters.status,
  });

  const emptyListProps = () => {
    switch (statusFilter) {
      case StatusFilterTab.Drafts:
        return {
          header: "No drafts yet",
          message: "Let's start looping in our residents. It's as simple as one-two-three.",
        };
      case StatusFilterTab.Pending:
        return {
          header: "Your work is done!",
          message: "No notifications pending approval or rejected!",
          showCreateButton: false,
        };
      case StatusFilterTab.Scheduled:
        return {
          header: "No scheduled notifications",
          message: "Let's start looping in our residents. It's as simple as one-two-three.",
        };
      case StatusFilterTab.Active:
        return {
          header: "No active notifications",
          message: "When a message is sent and still viewable by the public, it will show up here.",
          showCreateButton: false,
        };
      default:
        return {
          header: "No Notifications yet",
          message: "Let's start looping in our residents. It's as simple as one-two-three.",
        };
    }
  };

  const shouldShowLoader = isNotificationContentFetching || filters !== debouncedFilters;

  // Main empty state shows when no notifications have been created for this site at all- hides headers/filters
  const isFilterApplied = !!queryFilter || !!subscriptionGroupFilter || !!departmentFilter;
  const shouldShowMainEmptyState =
    totalNotifications === 0 && statusFilter === StatusFilterTab.All && !shouldShowLoader && !isFilterApplied;

  // Sub empty state shows when there are no notifications in a certain status
  const shouldShowSubEmptyState = totalNotifications === 0 && !shouldShowLoader;

  return (
    <>
      <FilterFlyout
        isOpen={isFilterFlyoutOpen}
        close={closeFilterFlyout}
        onSubmit={({ subscriptionGroupFilter, departmentGroupFilter }) => {
          setSubscriptionGroupFilter(subscriptionGroupFilter?.value ?? null);
          setDepartmentFilter(departmentGroupFilter?.value ?? null);
        }}
      />
      <NotifyTopNav />
      {shouldShowMainEmptyState && (
        <Message info className='tw-m-4'>
          <Message.Header>All your outgoing messages</Message.Header>
          <Message.Content>Everything you send, draft, and schedule can now be found here</Message.Content>
        </Message>
      )}
      <div className='tw-relative tw-flex tw-min-h-screen tw-flex-col'>
        {!shouldShowMainEmptyState ? (
          <>
            <div className='tw-sticky tw-top-0 tw-z-50' ref={titleBarRef}>
              <div className='sm:tw-mx-4 sm:tw-my-2'>
                <Segment
                  className={classNames(
                    "tw-grid tw-grid-cols-[1fr_auto] tw-grid-rows-[auto_auto_auto_auto] tw-gap-3 tw-rounded-none tw-border-x-0 tw-transition-shadow tw-duration-300 tw-ease-out [grid-template-areas:'header_create'_'tab_tab'_'query_query'_'filters_view'] sm:tw-rounded-[4px] sm:tw-border-x-[1px] md:tw-grid-cols-[1fr_1fr_auto] md:tw-grid-rows-[auto_auto_auto] md:tw-gap-y-0 md:[grid-template-areas:'header_header_create'_'tab_tab_none'_'query_filters_view'] lg:tw-grid-cols-[auto_1fr_auto] lg:tw-grid-rows-[auto_auto] lg:[grid-template-areas:'header_tab_create'_'query_query_view']",
                    {
                      "tw-shadow-lg": !isScrollPositionTop,
                    },
                  )}
                >
                  <div className='[grid-area:header]'>
                    <Header size='large' className='tw-m-0'>
                      Notifications
                    </Header>
                  </div>
                  <div className='tw-overflow-x-auto [grid-area:tab] md:tw-mb-6 md:tw-flex lg:tw-m-0'>
                    <TabBar
                      value={statusFilter}
                      onChange={(key) => {
                        setStatusFilter(key as StatusFilterTab);
                        if (key === StatusFilterTab.Drafts) setDepartmentFilter(null);
                      }}
                    >
                      <TabBar.TabList className='tw-flex tw-min-w-max tw-gap-2 tw-overflow-hidden'>
                        <TabBar.Tab id={StatusFilterTab.All}>All</TabBar.Tab>
                        <TabBar.Tab id={StatusFilterTab.Drafts}>Drafts</TabBar.Tab>
                        <TabBar.Tab id={StatusFilterTab.Pending}>
                          <div className='tw-flex tw-gap-2'>
                            <p>Pending</p>
                            {!!statuses?.Pending && (
                              <p className='tw-mt-[1px] tw-text-[13px] tw-text-[#4D4D4D]'>{statuses.Pending}</p>
                            )}
                          </div>
                        </TabBar.Tab>
                        <TabBar.Tab id={StatusFilterTab.Scheduled}>
                          <div className='tw-flex tw-gap-2'>
                            <p>Scheduled</p>
                            {!!statuses?.Scheduled && (
                              <p className='tw-mt-[1px] tw-text-[13px] tw-text-[#4D4D4D]'>{statuses.Scheduled}</p>
                            )}
                          </div>
                        </TabBar.Tab>
                        <TabBar.Tab id={StatusFilterTab.Active}>
                          <div className='tw-flex tw-gap-2'>
                            <p>Active</p>
                            {!!statuses?.Active && (
                              <p className='tw-mt-[1px] tw-text-[13px] tw-text-[#4D4D4D]'>{statuses.Active}</p>
                            )}
                          </div>
                        </TabBar.Tab>
                        <TabBar.Tab id={StatusFilterTab.Archived}>Archived</TabBar.Tab>
                      </TabBar.TabList>
                    </TabBar>
                  </div>
                  <div className='flex tw-items-center tw-justify-end [grid-area:create]'>
                    <Button
                      as={Link}
                      to={`/${site}/notify/notifications/create/from-template`}
                      primary
                      className='tw-m-0 tw-flex tw-gap-2 tw-whitespace-nowrap tw-px-3 sm:tw-px-6'
                    >
                      <Icon name='plus' className='tw-m-0 tw-text-white tw-opacity-100' />
                      <span className='tw-sr-only sm:tw-not-sr-only'>Create</span>
                    </Button>
                  </div>
                  <div className='tw-flex tw-gap-2 [grid-area:query] lg:tw-mt-3'>
                    <Input
                      className='tw-w-full md:tw-min-w-72 lg:tw-w-auto'
                      value={queryFilter}
                      onChange={(_, { value }) => setQueryFilter(value)}
                      placeholder='Search Posts'
                      icon='search'
                      iconPosition='left'
                      loading={shouldShowLoader}
                    />
                    <div className='tw-hidden tw-gap-2 lg:tw-flex'>
                      <AccessibleDropdown
                        className='tw-min-w-56'
                        value={subscriptionGroupFilterOption}
                        onChange={(option) =>
                          setSubscriptionGroupFilter(
                            (option as SingleValue<AccessibleDropdownOption<number>>)?.value ?? null,
                          )
                        }
                        options={subscriptionGroupOptions}
                        placeholder='Subscription Group'
                        clearable
                        isLoading={isSubscriptionGroupsLoading}
                      />
                      <AccessibleDropdown
                        key={departmentFilterOption?.key}
                        className='tw-min-w-56'
                        value={departmentFilterOption}
                        onChange={(option) =>
                          setDepartmentFilter((option as SingleValue<AccessibleDropdownOption<number>>)?.value ?? null)
                        }
                        options={departmentOptions}
                        clearable
                        placeholder='Department Group'
                        isLoading={isDepartmentsLoading}
                      />
                    </div>
                  </div>
                  <div className='tw-flex tw-gap-2 tw-whitespace-nowrap [grid-area:filters] lg:tw-hidden'>
                    <Button secondary className='tw-m-0' onClick={() => setShouldShowCheckboxes(!shouldShowCheckboxes)}>
                      Select
                    </Button>
                    <Button
                      className='button-ghost black tw-m-0 tw-space-x-2 tw-whitespace-nowrap'
                      onClick={openFilterFlyout}
                    >
                      <FontAwesomeIcon icon={faFilter} />
                      <span>Filters</span>
                    </Button>
                  </div>
                  <div className='tw-flex tw-items-center tw-justify-end [grid-area:view] lg:tw-mt-3'>
                    <IconToggleTabBar value={view} onChange={(key) => setView(key as ViewToggleTab)}>
                      <IconToggleTabBar.Tab id={ViewToggleTab.Grid} icon={faTable}>
                        Grid
                      </IconToggleTabBar.Tab>
                      <IconToggleTabBar.Tab id={ViewToggleTab.List} icon={faList}>
                        List
                      </IconToggleTabBar.Tab>
                    </IconToggleTabBar>
                  </div>
                </Segment>
              </div>
            </div>
            {!shouldShowSubEmptyState ? (
              <div className='tw-m-3 sm:tw-mx-4 sm:tw-my-2'>
                {view === ViewToggleTab.Grid && (
                  <NotifyNotificationsCardInfiniteList
                    filters={debouncedFilters}
                    showCheckboxes={shouldShowCheckboxes}
                  />
                )}
                {view === ViewToggleTab.List && (
                  <NotifyNotificationsTableInfiniteList titleBarRef={titleBarRef} filters={debouncedFilters} />
                )}
              </div>
            ) : (
              <div className='tw-mb-20 tw-flex tw-flex-1 tw-flex-col tw-items-center  tw-justify-center'>
                <NotifyNotificationsEmptyList {...emptyListProps()} />
              </div>
            )}
          </>
        ) : (
          <div className='tw-mb-40 tw-flex tw-h-full tw-flex-1 tw-flex-col tw-items-center tw-justify-center'>
            <NotifyNotificationsEmptyList
              header='No Notifications yet'
              message="Let's start looping in our residents. It's as simple as one-two-three."
            />
          </div>
        )}
      </div>
    </>
  );
}

function FilterFlyout({
  isOpen,
  close,
  onSubmit = () => {},
}: {
  isOpen: boolean;
  close: () => void;
  onSubmit?: (data: {
    subscriptionGroupFilter: AccessibleDropdownOption<number> | null;
    departmentGroupFilter: AccessibleDropdownOption<number> | null;
  }) => void;
}) {
  const [subscriptionGroupFilter, setSubscriptionGroupFilter] = useState<AccessibleDropdownOption<number> | null>(null);
  const [departmentGroupFilter, setDepartmentGroupFilter] = useState<AccessibleDropdownOption<number> | null>(null);

  const { subscriptionGroups, isSubscriptionGroupsLoading } = useNotifySubscriptionGroups();
  const {
    departmentsQuery: { data: departments, isLoading: isDepartmentsLoading },
  } = useCoreDepartments();

  return (
    <Flyout open={isOpen} onClose={close}>
      <Flyout.Header className='tw-flex tw-justify-between'>
        <div className='tw-flex tw-items-center tw-gap-3'>
          <Button className='button-ghost black center-icon tw-m-0 tw-p-3' onClick={close}>
            <FontAwesomeIcon icon={faXmarkLarge} />
            <span className='tw-sr-only'>Close</span>
          </Button>
          <Header className='tw-m-0'>Add Filters</Header>
        </div>
        <div className='tw-flex tw-items-center tw-gap-3'>
          <Button
            className='button-ghost tw-m-0'
            onClick={() => {
              setSubscriptionGroupFilter(null);
              setDepartmentGroupFilter(null);
            }}
          >
            Clear
          </Button>
          <Button
            primary
            className='tw-m-0'
            onClick={() => {
              onSubmit({
                subscriptionGroupFilter,
                departmentGroupFilter,
              });
              close();
            }}
          >
            Apply
          </Button>
        </div>
      </Flyout.Header>
      <Flyout.Body>
        <Form
          onSubmit={() => {
            onSubmit({
              subscriptionGroupFilter,
              departmentGroupFilter,
            });
            close();
          }}
        >
          <Form.Field>
            <AccessibleDropdown
              value={subscriptionGroupFilter}
              onChange={(option) => setSubscriptionGroupFilter(option as SingleValue<AccessibleDropdownOption<number>>)}
              options={
                subscriptionGroups?.map((group) => ({
                  key: group.id,
                  value: group.id,
                  label: group.name,
                })) ?? []
              }
              label='Subscription Group'
              clearable
              isLoading={isSubscriptionGroupsLoading}
            />
          </Form.Field>
          <Form.Field>
            <AccessibleDropdown
              value={departmentGroupFilter}
              onChange={(option) => setDepartmentGroupFilter(option as SingleValue<AccessibleDropdownOption<number>>)}
              options={
                departments?.map((department) => ({
                  key: department.id,
                  value: department.id,
                  label: department.name,
                })) ?? []
              }
              clearable
              label='Department Group'
              isLoading={isDepartmentsLoading}
            />
          </Form.Field>
        </Form>
      </Flyout.Body>
    </Flyout>
  );
}
